import { useEffect, useRef, useState } from "react";
import {
  IRONFLEET_TEAM,
  memberHasGps,
  memberIsOnline,
  teamMemberFromPresence,
  tileColor,
  type TeamMember,
} from "./teamColors";
import type { BoothCheckIn, PresenceRecord } from "../types";

const TILE_SCROLL = 132;

export function TeamBrowser({
  presence,
  selfUserId,
  selfEmail,
  selfGpsOn,
  selfOnline,
  showTeamGps,
  onShowTeamGpsChange,
  onOpenMember,
}: {
  presence: PresenceRecord[];
  selfUserId?: string;
  selfEmail?: string;
  selfGpsOn: boolean;
  selfOnline: boolean;
  showTeamGps: boolean;
  onShowTeamGpsChange: (show: boolean) => void;
  onOpenMember?: (member: TeamMember) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLUListElement>(null);
  const [peekEmail, setPeekEmail] = useState<string | null>(null);
  const [peekLeft, setPeekLeft] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const peekTimer = useRef<number>(0);
  const peeked = peekEmail ? IRONFLEET_TEAM.find((member) => member.email === peekEmail) : undefined;
  const peekedCheckIns = peeked
    ? (presence.find((entry) => teamMemberFromPresence(entry)?.email === peeked.email)?.check_ins ?? []).slice(0, 3)
    : [];

  const updateCarousel = () => {
    const el = stackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  const placePeek = (el: HTMLElement) => {
    const root = rootRef.current;
    if (!root) return;
    const tileRect = el.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    setPeekLeft(tileRect.left + tileRect.width / 2 - rootRect.left);
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(peekTimer.current);
    };
  }, []);

  useEffect(() => {
    const el = stackRef.current;
    if (!el) return;
    updateCarousel();
    const onScroll = () => {
      updateCarousel();
      setPeekEmail(null);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    const observer = new ResizeObserver(updateCarousel);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  const showPeek = (email: string, el: HTMLElement) => {
    window.clearTimeout(peekTimer.current);
    placePeek(el);
    setPeekEmail(email);
  };
  const hidePeek = () => {
    window.clearTimeout(peekTimer.current);
    peekTimer.current = window.setTimeout(() => setPeekEmail(null), 140);
  };
  const pageTiles = (dir: -1 | 1) => {
    stackRef.current?.scrollBy({ left: dir * TILE_SCROLL, behavior: "smooth" });
  };

  return (
    <div ref={rootRef} className="team-browser">
      <div className={`team-browser-rail${canScrollLeft ? " can-left" : ""}${canScrollRight ? " can-right" : ""}`}>
        {canScrollLeft ? (
          <button
            type="button"
            className="team-browser-page prev"
            aria-label="Previous teammates"
            onClick={() => pageTiles(-1)}
          >
            <Chevron dir="left" />
          </button>
        ) : null}
        <ul ref={stackRef} className="team-browser-stack" aria-label="Team">
          {IRONFLEET_TEAM.map((member) => (
            <li key={member.email}>
              <MemberTile
                member={member}
                presence={presence}
                selfEmail={selfEmail}
                selfUserId={selfUserId}
                selfGpsOn={selfGpsOn}
                selfOnline={selfOnline}
                onPeekEnter={(el) => showPeek(member.email, el)}
                onPeekLeave={hidePeek}
                onClick={() => {
                  setPeekEmail(null);
                  onOpenMember?.(member);
                }}
              />
            </li>
          ))}
        </ul>
        {canScrollRight ? (
          <button
            type="button"
            className="team-browser-page next"
            aria-label="Next teammates"
            onClick={() => pageTiles(1)}
          >
            <Chevron dir="right" />
          </button>
        ) : null}
      </div>
      <div className="map-team-key">
        <label className="map-team-toggle">
          <input
            type="checkbox"
            checked={showTeamGps}
            onChange={(event) => onShowTeamGpsChange(event.target.checked)}
          />
          Show team GPS
        </label>
      </div>
      {peeked ? (
        <CheckInPeek
          name={peeked.name}
          checkIns={peekedCheckIns}
          left={peekLeft}
          onEnter={() => {
            window.clearTimeout(peekTimer.current);
            setPeekEmail(peeked.email);
          }}
          onLeave={hidePeek}
        />
      ) : null}
    </div>
  );
}

function MemberTile({
  member,
  presence,
  selfEmail,
  selfUserId,
  selfGpsOn,
  selfOnline,
  onPeekEnter,
  onPeekLeave,
  onClick,
}: {
  member: TeamMember;
  presence: PresenceRecord[];
  selfEmail?: string;
  selfUserId?: string;
  selfGpsOn: boolean;
  selfOnline: boolean;
  onPeekEnter?: (el: HTMLElement) => void;
  onPeekLeave?: () => void;
  onClick?: () => void;
}) {
  const gpsOn = memberHasGps(member, presence, { selfEmail, selfUserId, selfGpsOn });
  const online = memberIsOnline(member, presence, { selfEmail, selfUserId, selfOnline });
  const color = tileColor(member, gpsOn);
  const isSelf = isSelfMember(member, selfEmail, selfUserId, presence);
  return (
    <button
      type="button"
      className={`team-tile-wrap${isSelf ? " self" : ""}`}
      aria-label={`${member.name} activity`}
      onMouseEnter={(event) => onPeekEnter?.(event.currentTarget)}
      onMouseLeave={onPeekLeave}
      onFocus={(event) => onPeekEnter?.(event.currentTarget)}
      onBlur={onPeekLeave}
      onClick={onClick}
    >
      <span
        className={`team-tile${gpsOn ? "" : " gps-off"}`}
        style={{ background: color.fill, borderColor: color.stroke }}
      >
        {member.initials}
      </span>
      {online ? <span className="team-online-dot" aria-hidden="true" /> : null}
    </button>
  );
}

function CheckInPeek({
  name,
  checkIns,
  left,
  onEnter,
  onLeave,
}: {
  name: string;
  checkIns: BoothCheckIn[];
  left: number;
  onEnter?: () => void;
  onLeave?: () => void;
}) {
  return (
    <div
      className="team-checkin-peek"
      role="tooltip"
      style={{ left }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="team-roster-head">{name}</div>
      {checkIns.length ? (
        <ul className="team-checkin-list">
          {checkIns.map((entry) => (
            <li key={`${entry.booth_id}-${entry.at}`}>
              <strong>{entry.booth_label ? `${entry.booth_label} ${entry.booth_id}` : `Booth ${entry.booth_id}`}</strong>
              <span className="muted">{relativeTime(entry.at)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted team-roster-empty">No recent check-ins.</p>
      )}
    </div>
  );
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        fill="currentColor"
        d={dir === "left" ? "M15.4 5.6 9 12l6.4 6.4 1.4-1.4L11.8 12l5-4.96z" : "M8.6 5.6 15 12l-6.4 6.4-1.4-1.4L12.2 12l-5-4.96z"}
      />
    </svg>
  );
}

function isSelfMember(
  member: TeamMember | undefined,
  selfEmail: string | undefined,
  selfUserId: string | undefined,
  presence: PresenceRecord[],
): boolean {
  if (!member) return false;
  if (selfEmail && member.email.toLowerCase() === selfEmail.trim().toLowerCase()) return true;
  if (!selfUserId) return false;
  const self = presence.find((entry) => entry.user_id === selfUserId);
  return Boolean(self && (self.email ?? "").toLowerCase() === member.email.toLowerCase());
}

export function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return "";
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
